import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';

import {
    Box, Text, Button, Divider, Link
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import Actions from 'Actions';
import UserActions from 'actions/UserActions';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import linkTrackingError from 'analytics/bindings/pages/all/linkTrackingError';
import { colors, space } from 'style/config';
import RegisterEN from 'components/Register/locales/Register_en_US';
import RegisterFR from 'components/Register/locales/Register_fr_CA';
const getText = localeUtils.getLocalisedDataFromResource(RegisterEN, RegisterFR);

const RegisterStoreMember = ({ email, handleSignIn, isOAuth2Flow }) => {
    const dispatch = useDispatch();

    // AC 3: Fire s.t()/TrackState on Check Your Email page load for ChatGPT flow
    useEffect(() => {
        if (isOAuth2Flow) {
            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    pageName: anaConsts.CHATGPT_REGISTER_CHECK_EMAIL_PAGE_NAME,
                    pageType: anaConsts.PAGE_TYPES.REGISTER,
                    eventStrings: [anaConsts.Event.EVENT_313]
                }
            });
        }
    }, [isOAuth2Flow]);

    useEffect(() => {
        sendEmail(true);
    }, []);

    const sendEmail = async (isInitialEmailTrigger = false) => {
        if (!email) {
            dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('errorModalTitle'),
                    message: getText('errorResend'),
                    buttonText: getText('resendButton'),
                    callback: sendEmail,
                    cancelText: getText('okButton'),
                    showCancelButtonLeft: true
                })
            );

            return;
        }

        const token = undefined; // No token for resend email in store member flow

        try {
            await UserActions.sendVerificationEmail(email, token);

            if (!isInitialEmailTrigger) {
                // AC 3.3: Fire s.tl()/TrackAction for resend email success in ChatGPT flow
                if (isOAuth2Flow) {
                    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                        data: {
                            eventStrings: [anaConsts.Event.EVENT_313],
                            actionInfo: anaConsts.LinkData.CHATGPT_REGISTER_RESEND_SUCCESS
                        }
                    });
                }

                dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        title: getText('successModalTitle'),
                        message: getText('successResend'),
                        buttonText: getText('okButton')
                    })
                );
            }
        } catch {
            if (!isInitialEmailTrigger) {
                // AC 3.2: Fire s.tl()/TrackAction for resend email failure in ChatGPT flow
                if (isOAuth2Flow) {
                    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                        data: {
                            bindingMethods: linkTrackingError,
                            eventStrings: [anaConsts.Event.REGISTRATION_ATTEMPT, anaConsts.Event.EVENT_313],
                            pageName: anaConsts.CHATGPT_REGISTER_CHECK_EMAIL_PAGE_NAME,
                            pageType: anaConsts.PAGE_TYPES.REGISTER,
                            fieldErrors: `${anaConsts.CHATGPT} registration:resendEmail`,
                            errorMessages: getText('errorResendShort')
                        }
                    });
                }

                dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        title: getText('errorModalTitle'),
                        message: getText('errorResend'),
                        buttonText: getText('resendButton'),
                        callback: sendEmail,
                        cancelText: getText('okButton'),
                        showCancelButtonLeft: true
                    })
                );
            }
        }
    };

    const resendEmail = () => {
        // AC 3.1: Fire s.tl()/TrackAction for send again click in ChatGPT flow
        if (isOAuth2Flow) {
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    eventStrings: [anaConsts.Event.EVENT_313],
                    actionInfo: anaConsts.LinkData.CHATGPT_REGISTER_SEND_AGAIN
                }
            });
        }

        sendEmail(false);
    };

    return (
        <Box
            lineHeight='tight'
            width='100%'
        >
            <Text
                is='h2'
                fontWeight='bold'
                fontSize='xl'
                marginBottom={4}
                children={getText('inStoreMemberTitle')}
            />
            <Text
                is='p'
                marginBottom={4}
            >
                {getText('sentVerificationLink')}{' '}
                <Text
                    is='span'
                    fontWeight='bold'
                >
                    {email}
                </Text>
                .
            </Text>
            <Text
                is='p'
                marginBottom={3}
                fontSize='base'
                lineHeight='base'
                fontWeight='bold'
                children={getText('nextStep')}
            />

            <Box
                is='ol'
                css={{
                    listStyle: 'none',
                    paddingLeft: 0,
                    counterReset: 'step-counter',
                    '& li': {
                        counterIncrement: 'step-counter',
                        position: 'relative',
                        paddingLeft: space[6],
                        '&::before': {
                            content: 'counter(step-counter)',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#eee',
                            color: colors.black,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '14px'
                        }
                    }
                }}
            >
                <Text
                    is='li'
                    marginBottom={2}
                    children={getText('step1')}
                />
                <Text
                    is='li'
                    marginBottom={2}
                    children={getText('step2')}
                />
                <Text
                    is='li'
                    marginBottom={4}
                    children={getText('step3')}
                />
            </Box>
            <Button
                variant='primary'
                width='100%'
                type='button'
                onClick={handleSignIn}
            >
                {getText('gotIt')}
            </Button>
            <Divider
                marginBottom={4}
                marginTop={4}
            />
            <Text marginBottom={4}>
                {getText('emailMissed')}
                <Link
                    onClick={resendEmail}
                    color={colors.blue}
                    underline={true}
                >
                    {getText('resend')}
                </Link>
            </Text>
        </Box>
    );
};

export default wrapFunctionalComponent(RegisterStoreMember, 'RegisterStoreMember');
